import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, setAccessToken } from '@/api/client';
import { AuthContext, type AuthState, type Identity } from '@/lib/authContext';

/**
 * Authentication state for the SPA.
 *
 * The access token is held in memory only — never localStorage. A token in
 * localStorage is readable by any script on the page, so an XSS becomes durable
 * session theft. Held in memory it dies with the tab, and the httpOnly refresh
 * cookie is what survives a reload (the server sets it; JavaScript cannot read
 * it).
 *
 * The cost is one refresh call per page load, which is the right trade.
 */

interface LoginResult {
  access_token?: string;
  token_type?: string;
  expires_at?: string;
  mfa_required: boolean;
  /** The user's role mandates MFA and no factor is enrolled yet. */
  enrolment_required?: boolean;
  challenge_token?: string;
}

interface EnrolmentResult {
  uri: string;
  secret: string;
  recovery_codes: string[];
}

/** Refresh slightly before the 15-minute expiry, so a request never races it. */
const REFRESH_MARGIN_MS = 60_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [initialising, setInitialising] = useState(true);
  const [pendingChallenge, setPendingChallenge] = useState<string | null>(null);
  const [workspace, setWorkspace] = useState<string | null>(
    () => sessionStorage.getItem('hrms.workspace'),
  );

  const refreshTimer = useRef<number | undefined>(undefined);
  // Holds the latest refreshSession so the scheduler can call it without the
  // two callbacks depending on each other, which would be a cycle.
  const refreshRef = useRef<() => Promise<boolean>>(async () => false);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setIdentity(null);
    setPendingChallenge(null);
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = undefined;
    }
  }, []);

  const loadIdentity = useCallback(async () => {
    setIdentity(await api.get<Identity>('/me'));
  }, []);

  const scheduleRefresh = useCallback((expiresAt?: string) => {
    if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    if (!expiresAt) return;

    const delay = new Date(expiresAt).getTime() - Date.now() - REFRESH_MARGIN_MS;
    refreshTimer.current = window.setTimeout(() => {
      void refreshRef.current();
    }, Math.max(delay, 5_000));
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const result = await api.post<LoginResult>('/auth/refresh');
      if (result.access_token) {
        setAccessToken(result.access_token);
        scheduleRefresh(result.expires_at);
        await loadIdentity();
        return true;
      }
    } catch {
      // A failed refresh means no valid session — including after the server
      // revoked the family on reuse detection. Clearing is the right response.
      clearSession();
    }
    return false;
  }, [clearSession, loadIdentity, scheduleRefresh]);

  refreshRef.current = refreshSession;

  // On mount, try to resume the session from the httpOnly refresh cookie.
  useEffect(() => {
    void (async () => {
      await refreshRef.current();
      setInitialising(false);
    })();
    return () => {
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    };
  }, []);

  const login = useCallback(
    async (ws: string, email: string, password: string) => {
      const result = await api.post<LoginResult>('/auth/login', {
        tenant: ws,
        email,
        password,
      });

      // The workspace is remembered for the MFA step and for convenience next
      // time. It is not a secret.
      sessionStorage.setItem('hrms.workspace', ws);
      setWorkspace(ws);

      // Enrolment is checked first: a user who must enrol has no factor to
      // verify against, so offering the code step would be a dead end.
      if (result.enrolment_required) {
        setPendingChallenge(result.challenge_token ?? null);
        return { mfaRequired: false, enrolmentRequired: true };
      }

      if (result.mfa_required) {
        setPendingChallenge(result.challenge_token ?? null);
        return { mfaRequired: true, enrolmentRequired: false };
      }

      if (result.access_token) {
        setAccessToken(result.access_token);
        scheduleRefresh(result.expires_at);
        await loadIdentity();
      }
      return { mfaRequired: false, enrolmentRequired: false };
    },
    [loadIdentity, scheduleRefresh],
  );

  const requireChallenge = useCallback(() => {
    if (!pendingChallenge || !workspace) {
      throw new ApiError({
        type: 'about:blank',
        title: 'Session expired',
        status: 401,
        detail: 'Please sign in again.',
      });
    }
    return { challenge: pendingChallenge, tenant: workspace };
  }, [pendingChallenge, workspace]);

  /**
   * Starts enrolment and returns the secret, QR URI and recovery codes.
   *
   * None of it is persisted here. The recovery codes live in component state
   * only until confirmation posts them back for hashing — writing them to
   * storage would leave a bypass of the second factor sitting in the browser.
   */
  const beginEnrolment = useCallback(
    async (replace = false) => {
      const { challenge, tenant } = requireChallenge();
      return api.post<EnrolmentResult>('/auth/mfa/enrol', {
        tenant,
        challenge_token: challenge,
        replace,
      });
    },
    [requireChallenge],
  );

  const confirmEnrolment = useCallback(
    async (code: string, recoveryCodes: string[]) => {
      const { challenge, tenant } = requireChallenge();

      const result = await api.post<LoginResult>('/auth/mfa/enrol/confirm', {
        tenant,
        challenge_token: challenge,
        code,
        recovery_codes: recoveryCodes,
      });

      // Confirming enrolment completes the login it interrupted, so the user
      // lands signed in rather than being sent back to the form.
      if (result.access_token) {
        setAccessToken(result.access_token);
        setPendingChallenge(null);
        scheduleRefresh(result.expires_at);
        await loadIdentity();
      }
    },
    [requireChallenge, loadIdentity, scheduleRefresh],
  );

  const recoverWithCode = useCallback(
    async (code: string) => {
      const { challenge, tenant } = requireChallenge();

      const result = await api.post<LoginResult>('/auth/mfa/recover', {
        tenant,
        challenge_token: challenge,
        code,
      });

      if (result.access_token) {
        setAccessToken(result.access_token);
        setPendingChallenge(null);
        scheduleRefresh(result.expires_at);
        await loadIdentity();
      }
    },
    [requireChallenge, loadIdentity, scheduleRefresh],
  );

  const verifyMfa = useCallback(
    async (code: string) => {
      const { challenge, tenant } = requireChallenge();

      const result = await api.post<LoginResult>('/auth/mfa/verify', {
        tenant,
        challenge_token: challenge,
        code,
      });

      if (result.access_token) {
        setAccessToken(result.access_token);
        setPendingChallenge(null);
        scheduleRefresh(result.expires_at);
        await loadIdentity();
      }
    },
    [requireChallenge, loadIdentity, scheduleRefresh],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      // Clear locally even if the call failed: the user's intent is to end the
      // session, and the cookie is cleared server-side regardless.
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthState>(
    () => ({
      identity,
      initialising,
      pendingChallenge,
      workspace,
      login,
      verifyMfa,
      beginEnrolment,
      confirmEnrolment,
      recoverWithCode,
      logout,
    }),
    [
      identity,
      initialising,
      pendingChallenge,
      workspace,
      login,
      verifyMfa,
      beginEnrolment,
      confirmEnrolment,
      recoverWithCode,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
